import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Calendar, TrendingUp, TrendingDown, Loader } from 'lucide-react';
import { logEngagementSharePercentage } from '../utils/debugUtils';

interface EngagementDataPoint {
  date: string;
  engagement_score: number;
  percent_change: number;
}

interface UserEngagementChartProps {
  username: string;
}

export function UserEngagementChart({ username }: UserEngagementChartProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d'>('7d');
  const [engagementData, setEngagementData] = useState<EngagementDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sevenDayEngagement, setSevenDayEngagement] = useState<number | null>(null);

  useEffect(() => {
    const fetchEngagementData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Debug: Log the engagement share percentage for the specified user and date
        if (username === 'Old_Samster') {
          await logEngagementSharePercentage('Old_Samster', '2025-05-30');
        }
        
        // Fetch 7-day engagement percentage - get the most recent document
        const engagementCollectionRef = collection(db, 'user_engagement_history', username, '7_day_engagement');
        const engagementQuery = query(engagementCollectionRef, orderBy('timestamp', 'desc'), limit(1));
        const engagementSnapshot = await getDocs(engagementQuery);
        
        if (!engagementSnapshot.empty) {
          const mostRecentDoc = engagementSnapshot.docs[0];
          const data = mostRecentDoc.data();
          setSevenDayEngagement(data.engagement_share_percentage || null);
          console.log(`Found most recent 7-day engagement for ${username} from ${mostRecentDoc.id}:`, data.engagement_share_percentage);
        } else {
          // Fallback to the specific date if no documents found in the collection
          const specificDateRef = doc(db, 'user_engagement_history', username, '7_day_engagement', '2025-05-30');
          const specificDateSnap = await getDoc(specificDateRef);
          
          if (specificDateSnap.exists()) {
            const data = specificDateSnap.data();
            setSevenDayEngagement(data.engagement_share_percentage || null);
            console.log(`Using fallback 7-day engagement for ${username}:`, data.engagement_share_percentage);
          } else {
            console.log(`No 7-day engagement data found for ${username}`);
          }
        }
        
        // Calculate date range based on selected time range
        const endDate = new Date();
        const startDate = new Date();
        
        switch (timeRange) {
          case '7d':
            startDate.setDate(endDate.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(endDate.getDate() - 30);
            break;
        }
        
        // Format dates for Firestore queries
        const formatDate = (date: Date) => {
          return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        };
        
        const startDateStr = formatDate(startDate);
        const endDateStr = formatDate(endDate);
        
        // Query Firestore for engagement data
        const engagementRef = collection(db, 'user_engagement_history', username, 'daily');
        
        // If we're querying for a specific date range
        const q = query(
          engagementRef,
          where('date', '>=', startDateStr),
          where('date', '<=', endDateStr),
          orderBy('date', 'asc')
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          // If no data in the date range, try to get the most recent entries
          const fallbackQuery = query(
            engagementRef,
            orderBy('date', 'desc'),
            limit(timeRange === '7d' ? 7 : 30)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          
          if (fallbackSnapshot.empty) {
            setError('No engagement data available for this user');
            setEngagementData([]);
          } else {
            // Convert to our data format and sort by date (ascending)
            const data = fallbackSnapshot.docs.map(doc => ({
              date: doc.id,
              engagement_score: doc.data().engagement_score || 0,
              percent_change: doc.data().percent_change || 0
            })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            
            setEngagementData(data);
          }
        } else {
          // Convert to our data format
          const data = querySnapshot.docs.map(doc => ({
            date: doc.id,
            engagement_score: doc.data().engagement_score || 0,
            percent_change: doc.data().percent_change || 0
          }));
          
          setEngagementData(data);
        }
      } catch (error) {
        console.error('Error fetching engagement data:', error);
        setError('Failed to load engagement data');
        setEngagementData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEngagementData();
  }, [username, timeRange]);

  // Calculate overall trend
  const calculateTrend = () => {
    if (engagementData.length < 2) return 0;
    
    const firstValue = engagementData[0].engagement_score;
    const lastValue = engagementData[engagementData.length - 1].engagement_score;
    
    if (firstValue === 0) return 0;
    return ((lastValue - firstValue) / firstValue) * 100;
  };

  const overallTrend = calculateTrend();

  // Prepare chart data
  const chartData = {
    labels: engagementData.map(item => item.date),
    datasets: [
      {
        label: 'Engagement Score',
        data: engagementData.map(item => item.engagement_score),
        borderColor: 'rgba(6, 182, 212, 1)', // Cyan color
        backgroundColor: 'rgba(6, 182, 212, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: timeRange === '7d' ? 'day' as const : 'week' as const,
          tooltipFormat: 'PPP',
          displayFormats: {
            day: 'MMM d',
            week: 'MMM d'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          },
          maxRotation: 0
        },
        border: {
          display: false
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          },
          callback: (value: number) => value.toLocaleString()
        },
        border: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'rgba(255, 255, 255, 0.8)',
        bodyColor: 'rgba(255, 255, 255, 0.8)',
        callbacks: {
          title: (context: any) => {
            return new Date(context[0].label).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          },
          label: (context: any) => {
            const dataIndex = context.dataIndex;
            const data = engagementData[dataIndex];
            
            return [
              `Engagement: ${context.raw.toLocaleString()}`,
              `Change: ${data.percent_change >= 0 ? '+' : ''}${data.percent_change.toFixed(2)}%`
            ];
          }
        }
      }
    },
    elements: {
      point: {
        radius: 2,
        hitRadius: 10,
        hoverRadius: 5,
        backgroundColor: 'rgba(6, 182, 212, 1)',
        borderColor: 'rgba(255, 255, 255, 1)',
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold">Engagement History</h3>
        </div>
        
        <div className="flex items-center space-x-1 bg-black/20 rounded-lg p-1">
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-3 py-1 rounded-lg text-xs ${
              timeRange === '7d' ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-3 py-1 rounded-lg text-xs ${
              timeRange === '30d' ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            30 Days
          </button>
        </div>
      </div>

      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center">
            <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            {error}
          </div>
        ) : engagementData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            No engagement data available
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Latest: {engagementData[engagementData.length - 1]?.engagement_score.toLocaleString() || 'N/A'}
              </div>
              {sevenDayEngagement !== null && (
                <div className="text-sm text-gray-400">
                  7-Day Share: <span className="text-cyan-400 font-medium">{sevenDayEngagement.toFixed(2)}%</span>
                </div>
              )}
            </div>
            
            <div className="h-[300px]">
              <Line data={chartData} options={chartOptions} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}